import React from 'react';
import { Box, Button, Checkbox, IconButton, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { emptyBreakdownClass, type SandboxBreakdownClass } from '../../utils/sandboxCurriculum';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: '#323232',
        color: OBSIDIAN.onSurface,
        fontFamily: OBSIDIAN_BODY_FONT,
        fontSize: 14,
        borderRadius: 2,
        '& fieldset': { borderColor: `${OBSIDIAN.outline}55` },
        '&:hover fieldset': { borderColor: OBSIDIAN.outline },
        '&.Mui-focused fieldset': { borderColor: OBSIDIAN.primary },
    },
    '& .MuiInputBase-input': {
        color: OBSIDIAN.onSurface,
        '&::placeholder': { color: OBSIDIAN.onSurfaceVariant, opacity: 1 },
    },
};

type Props = {
    classes: SandboxBreakdownClass[];
    onChange: (next: SandboxBreakdownClass[]) => void;
};

export function SandboxCourseBreakdownEditor({ classes, onChange }: Props) {
    const update = (index: number, patch: Partial<SandboxBreakdownClass>) => {
        onChange(classes.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    return (
        <Box>
            <Typography
                sx={{
                    mb: 0.5,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: OBSIDIAN.primary,
                    fontFamily: OBSIDIAN_BODY_FONT,
                }}>
                Course breakdown
            </Typography>
            <Typography
                sx={{ mb: 1.5, fontSize: 12, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                Add each class and a short description. Curriculum on the course page is built from this list.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {classes.map((row, index) => (
                    <Box
                        key={row.key}
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: OBSIDIAN.surfaceContainer,
                            border: `1px solid ${OBSIDIAN.outline}22`,
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.06em',
                                    color: OBSIDIAN.onSurfaceVariant,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                }}>
                                CLASS {index + 1}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => onChange(classes.filter((_, i) => i !== index))}
                                sx={{ color: OBSIDIAN.onSurfaceVariant }}
                                aria-label="Remove class">
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Class title"
                            value={row.title}
                            onChange={e => update(index, { title: e.target.value })}
                            sx={{ ...fieldSx, mb: 1 }}
                        />
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            placeholder="What students will learn in this class"
                            value={row.description}
                            onChange={e => update(index, { description: e.target.value })}
                            sx={fieldSx}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
                            <Box
                                component="label"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                                <Checkbox
                                    size="small"
                                    checked={row.is_free_preview}
                                    onChange={e => update(index, { is_free_preview: e.target.checked })}
                                    sx={{
                                        color: OBSIDIAN.outline,
                                        '&.Mui-checked': { color: OBSIDIAN.secondary },
                                        p: 0.5,
                                    }}
                                />
                                <Typography sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant }}>
                                    Free preview
                                </Typography>
                            </Box>
                            <Box
                                component="label"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                                <Checkbox
                                    size="small"
                                    checked={row.has_assignment}
                                    onChange={e => update(index, { has_assignment: e.target.checked })}
                                    sx={{
                                        color: OBSIDIAN.outline,
                                        '&.Mui-checked': { color: OBSIDIAN.primary },
                                        p: 0.5,
                                    }}
                                />
                                <Typography sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant }}>
                                    Includes project
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>

            <Button
                startIcon={<AddIcon />}
                onClick={() => onChange([...classes, emptyBreakdownClass(classes.length)])}
                sx={{
                    mt: 1.25,
                    color: OBSIDIAN.primary,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                }}>
                Add class
            </Button>
        </Box>
    );
}
